import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useQuery } from '@tanstack/react-query';
import * as Routes from '@/modules/routes';
import QueryKeys from '@/utils/QueryKeys';
import SPASelect from '../../shared/SPASelect';
import filterSelectStyles from './filterSelectStyles';

export interface SubscriberOption {
  label: string;
  value: string;
}

interface SearchedSubscriber {
  id: number;
  name: string;
}

interface SubscriberSelectProps {
  inputId: string;
  onChange: (option: SubscriberOption | null) => void;
  value: SubscriberOption | null;
}

const SEARCH_DEBOUNCE_MS = 300;
const NO_OPTIONS: SubscriberOption[] = [];

export const subscriberOption = ({ id, name }: SearchedSubscriber): SubscriberOption => ({
  label: `${name} (${id})`, value: String(id),
});

const searchSubscribers = async (term: string) => {
  const payload = { only: ['id', 'name'], search: { name_or_id_string_cont: term } };
  const res = await axios.post<{ data: SearchedSubscriber[] }>(Routes.plato_api_search_subscribers_path(), payload);

  return res.data.data.map(subscriberOption);
};

const useDebounced = (value: string, ms: number) => {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(timer);
  }, [ms, value]);

  return debounced;
};

// Same search the QTI download modal uses (POST /api/v2/subscribers/search, name or id). The query
// only runs once the menu is open, so a page that stays on "All districts" never pays for it.
const SubscriberSelect = ({ inputId, onChange, value }: SubscriberSelectProps) => {
  const [input, setInput] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const term = useDebounced(input.trim(), SEARCH_DEBOUNCE_MS);

  const { data: options = NO_OPTIONS, isFetching } = useQuery({
    enabled: menuOpen || term !== '',
    keepPreviousData: true,
    queryFn: () => searchSubscribers(term),
    queryKey: [QueryKeys.SubscriberSearch, term],
  });

  return (
    <SPASelect<SubscriberOption>
      isClearable
      filterOption={null}
      inputId={inputId}
      inputValue={input}
      isLoading={isFetching}
      noOptionsMessage={() => (term ? 'No matching districts' : 'Type a district name or id')}
      options={options}
      placeholder="All districts"
      styles={filterSelectStyles}
      value={value}
      onChange={option => onChange(option)}
      onInputChange={(next, { action }) => setInput(action === 'input-change' ? next : '')}
      onMenuClose={() => setMenuOpen(false)}
      onMenuOpen={() => setMenuOpen(true)}
    />
  );
};

export default SubscriberSelect;
